/* tslint:disable:quotemark */
/*
 * Copyright (c) 2020 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import { Entity, EntityData, EntityFieldKey, EntityModel } from '@typescript-kit/data';
import { ObserveModes } from '@typescript-kit/core';
import { CountryCode } from './country-code.model';

export class CompanyFieldKey extends EntityFieldKey {
  static readonly ID: keyof CompanyData = 'id';
  static readonly NAME: keyof CompanyData = 'name';
  static readonly STREET: keyof CompanyData = 'street';
  static readonly ZIP_CODE: keyof CompanyData = 'zipCode';
  static readonly CITY: keyof CompanyData = 'city';
  static readonly COUNTRY_CODE: keyof CompanyData = 'countryCode';
  static readonly TAX_ID: keyof CompanyData = 'taxId';
  static readonly CONTACT_NAME: keyof CompanyData = 'contactName';
  static readonly CONTACT_EMAIL: keyof CompanyData = 'contactEmail';
  static readonly IS_ACTIVE: keyof CompanyData = 'isActive';
}

export interface CompanyData extends EntityData {
  id?: string;
  name?: string;
  street?: string;
  zipCode?: string;
  city?: string;
  countryCode?: CountryCode;
  taxId?: string;
  contactName?: string;
  contactEmail?: string;
  isActive?: boolean;
}

export class Company extends Entity implements CompanyData {
  readonly id: string;
  readonly name: string;
  readonly street: string;
  readonly zipCode: string;
  readonly city: string;
  readonly countryCode: CountryCode;
  readonly taxId: string;
  readonly contactName: string;
  readonly contactEmail: string;
  readonly isActive: boolean;

  constructor(data: CompanyData) {
    super(data);
    this.id = data.id;
    this.name = data.name;
    this.street = data.street;
    this.zipCode = data.zipCode;
    this.city = data.city;
    this.countryCode = data.countryCode;
    this.taxId = data.taxId;
    this.contactName = data.contactName;
    this.contactEmail = data.contactEmail;
    this.isActive = data.isActive;
  }
}

export class CompanyModel extends EntityModel implements CompanyData {
  static readonly KEYS = EntityModel.KEYS.concat(
    Object.keys(CompanyFieldKey).map((key) => CompanyFieldKey[key])
  );

  constructor(values?: CompanyData) {
    super(values, CompanyModel.KEYS, { observeMode: ObserveModes.FLAT });
  }

  get id(): string {
    return this.get(CompanyFieldKey.ID);
  }

  set id(value: string) {
    this.set(CompanyFieldKey.ID, value);
  }

  get name(): string {
    return this.get(CompanyFieldKey.NAME);
  }

  set name(value: string) {
    this.set(CompanyFieldKey.NAME, value);
  }

  get street(): string {
    return this.get(CompanyFieldKey.STREET);
  }

  set street(value: string) {
    this.set(CompanyFieldKey.STREET, value);
  }

  get zipCode(): string {
    return this.get(CompanyFieldKey.ZIP_CODE);
  }

  set zipCode(value: string) {
    this.set(CompanyFieldKey.ZIP_CODE, value);
  }

  get city(): string {
    return this.get(CompanyFieldKey.CITY);
  }

  set city(value: string) {
    this.set(CompanyFieldKey.CITY, value);
  }

  get countryCode(): CountryCode {
    return this.get(CompanyFieldKey.COUNTRY_CODE);
  }

  set countryCode(value: CountryCode) {
    this.set(CompanyFieldKey.COUNTRY_CODE, value);
  }

  get taxId(): string {
    return this.get(CompanyFieldKey.TAX_ID);
  }

  set taxId(value: string) {
    this.set(CompanyFieldKey.TAX_ID, value);
  }

  get contactName(): string {
    return this.get(CompanyFieldKey.CONTACT_NAME);
  }

  set contactName(value: string) {
    this.set(CompanyFieldKey.CONTACT_NAME, value);
  }

  get contactEmail(): string {
    return this.get(CompanyFieldKey.CONTACT_EMAIL);
  }

  set contactEmail(value: string) {
    this.set(CompanyFieldKey.CONTACT_EMAIL, value);
  }

  get isActive(): boolean {
    return this.get(CompanyFieldKey.IS_ACTIVE);
  }

  set isActive(value: boolean) {
    this.set(CompanyFieldKey.IS_ACTIVE, value);
  }
}

